import { useEffect, useRef } from 'react';
import type { EChartsCoreOption, EChartsType } from 'echarts/core';

let echartsPromise: Promise<typeof import('echarts/core')> | undefined;

function loadEcharts(): Promise<typeof import('echarts/core')> {
  echartsPromise ??= Promise.all([
    import('echarts/core'),
    import('echarts/charts'),
    import('echarts/components'),
    import('echarts/renderers'),
  ]).then(([echarts, charts, components, renderers]) => {
    echarts.use([
      components.AriaComponent,
      charts.BarChart,
      renderers.CanvasRenderer,
      components.GridComponent,
      components.LegendComponent,
      charts.LineChart,
      components.TooltipComponent,
    ]);
    return echarts;
  });
  return echartsPromise;
}

export function DashboardChart({
  option,
  label,
}: {
  option: EChartsCoreOption;
  label: string;
}): JSX.Element {
  const elementRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<EChartsType>();
  const optionRef = useRef(option);
  optionRef.current = option;

  useEffect(() => {
    const element = elementRef.current;
    if (element === null) return;
    let cancelled = false;
    let observer: ResizeObserver | undefined;
    const resize = (): void => chartRef.current?.resize();
    void loadEcharts().then((echarts) => {
      if (cancelled) return;
      const chart = echarts.init(element, undefined, { renderer: 'canvas' });
      chartRef.current = chart;
      chart.setOption(optionRef.current, { notMerge: true });
      observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(resize);
      observer?.observe(element);
      window.addEventListener('resize', resize);
    });
    return () => {
      cancelled = true;
      observer?.disconnect();
      window.removeEventListener('resize', resize);
      chartRef.current?.dispose();
      chartRef.current = undefined;
    };
  }, []); // The update effect applies later option changes.

  useEffect(() => {
    chartRef.current?.setOption(option, { notMerge: true });
  }, [option]);

  return <div ref={elementRef} className="tcvs-dashboard-echart" role="img" aria-label={label} />;
}
